import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  Injector,
  input,
  output,
  Signal,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TiptapEditorOutputFormat, TiptapTableOfContentsComponent } from '@examdojo/core/tiptap';
import { TiptapEditorWrapperComponent } from '@examdojo/core/tiptap-react-ui-components';
import type { UploadFunction } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-node/image-upload-node/image-upload-node-extension';
import { ImageUploadNode } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-node/image-upload-node/image-upload-node-extension';
import { MAX_FILE_SIZE } from '@examdojo/core/tiptap-react-ui-components/lib/tiptap-utils';
import { distinctUntilDeepChanged } from '@examdojo/rxjs';
import { EditorWithPopoverService, PopoverExtension, PopoverService } from '@examdojo/tiptap-popover';
import { isNotNullish } from '@examdojo/util/nullish';
import { JSONContent } from '@tiptap/core';
import type { Editor } from '@tiptap/react';
import { combineLatest, debounceTime, filter, map, merge, tap } from 'rxjs';
import { contentBlocksToTiptap, tiptapToContentBlocks } from '../converters';
import { ContentBlock } from '../model';
import { KeyPointCalloutExtension } from './extensions/key-point-callout';
import { SolverExtension } from './extensions/solver';
import { SuperLatexExtension } from './extensions/super-latex';
import { WidgetExtension } from './extensions/widget';
import { STUDY_NOTES_SLASH_MENU_ITEMS } from './slash-menu-items';

@Component({
  selector: 'dojo-study-notes-editor',
  templateUrl: './study-notes-editor.component.html',
  styleUrls: ['./study-notes-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block h-full' },
  imports: [TiptapEditorWrapperComponent, TiptapTableOfContentsComponent, ReactiveFormsModule],
  providers: [PopoverService],
})
export class StudyNotesEditorComponent {
  constructor(
    private readonly injector: Injector,
    private readonly popoverService: PopoverService,
  ) {
    merge(this.syncContentToEditor(), this.syncEditorToContentChange()).pipe(takeUntilDestroyed()).subscribe();
    effect(() => this.configurePopoverService());
  }

  readonly content = input<ContentBlock[]>([]);
  readonly editable = input<boolean>(true);
  readonly showTableOfContents = input<boolean>(false);
  readonly contentPadding = input<string>('48px 96px');
  readonly imageUploadFn = input<UploadFunction>();
  readonly resolveImageSrcFn = input<(src: string) => string>();
  readonly toRelativeImageSrcFn = input<(src: string) => string>();

  readonly contentChange = output<ContentBlock[]>();
  readonly editorReady = output<Editor>();

  readonly editor = signal<Editor | null>(null);
  private readonly editorWrapper =
    viewChild<TiptapEditorWrapperComponent<TiptapEditorOutputFormat.Json>>('editorWrapper');

  private readonly scrollableContainerRef = viewChild<ElementRef<HTMLElement>>('scrollableContainerRef');
  readonly scrollableContainer = computed(() => this.scrollableContainerRef()?.nativeElement);

  readonly editorControl = new FormControl<JSONContent>({}, { nonNullable: true });

  readonly editorOptions: Signal<Partial<Editor['options']>> = computed(() => ({
    extensions: [
      KeyPointCalloutExtension(this.injector),
      PopoverExtension(this.injector),
      SuperLatexExtension(this.injector),
      SolverExtension(this.injector),
      WidgetExtension(this.injector),
      ...(this.imageUploadFn()
        ? [
            ImageUploadNode.configure({
              accept: 'image/*',
              maxSize: MAX_FILE_SIZE,
              limit: 3,
              upload: this.stableImageUpload,
              onError: (error: Error) => console.error('Upload failed:', error),
            }),
          ]
        : []),
    ],
    editable: this.editable(),
    onCreate: ({ editor }: { editor: EditorWithPopoverService }) => {
      this.editor.set(editor);
      editor.storage.popoverService = this.popoverService;
      this.editorReady.emit(editor);
    },
  }));

  readonly tableOfContentsData = computed(() => this.editorWrapper()?.tableOfContentData() ?? []);

  readonly slashMenuItems = STUDY_NOTES_SLASH_MENU_ITEMS;
  readonly TiptapEditorOutputFormat = TiptapEditorOutputFormat;

  /**
   * Stable upload wrapper that delegates to the current `imageUploadFn` signal at call time.
   * This avoids the stale-closure problem: the Tiptap `useEditor` hook creates the editor
   * once and does not reconfigure extensions, so a closure captured at creation time would
   * always target the originally-selected file.
   */
  private readonly stableImageUpload: UploadFunction = (file, onProgress, abortSignal) => {
    const fn = this.imageUploadFn();
    if (!fn) {
      return Promise.reject(new Error('No image upload function configured'));
    }
    return fn(file, onProgress, abortSignal);
  };

  private syncContentToEditor() {
    return combineLatest([
      toObservable(this.content).pipe(distinctUntilDeepChanged()),
      toObservable(this.editor).pipe(filter(isNotNullish)),
    ]).pipe(
      tap(([blocks]) => {
        try {
          const resolve = this.resolveImageSrcFn();
          this.editorControl.setValue(
            contentBlocksToTiptap(blocks, resolve ? { transformImageSrc: resolve } : undefined),
            { emitEvent: false },
          );
        } catch (err) {
          console.error('Failed to convert ContentBlock[] to Tiptap:', err);
        }
      }),
    );
  }

  private syncEditorToContentChange() {
    return this.editorControl.valueChanges.pipe(
      debounceTime(300),
      map((value) => {
        const editor = this.editor();
        if (!editor) {
          return null;
        }
        try {
          const strip = this.toRelativeImageSrcFn();
          return tiptapToContentBlocks(value, strip ? { transformImageSrc: strip } : undefined);
        } catch (err) {
          console.error('Failed to convert Tiptap to ContentBlock[]:', err);
          return null;
        }
      }),
      filter(isNotNullish),
      distinctUntilDeepChanged(),
      tap((blocks) => this.contentChange.emit(blocks)),
    );
  }

  private configurePopoverService(): void {
    this.popoverService.config = {
      allowEditing: this.editable(),
      editorOptions: {
        extensions: [
          KeyPointCalloutExtension(this.injector),
          SuperLatexExtension(this.injector),
          SolverExtension(this.injector),
        ],
      },
      slashMenuItems: this.slashMenuItems,
      onContentChange: (popoverId, jsonContent) => {
        let parsedContent;
        try {
          parsedContent = JSON.parse(jsonContent);
        } catch {
          console.error('Failed to parse popover content change:', jsonContent);
          return;
        }
        this.editor()?.commands.updatePopoverById(popoverId, { jsonContent: parsedContent });
      },
      onLinkUrlChange: (popoverId, linkUrl) => {
        this.editor()?.commands.updatePopoverById(popoverId, { linkUrl: linkUrl || undefined });
      },
    };
  }
}
